import { useTranslation } from "react-i18next";
import { MobileCardRow, MobileCardWrapper, TableBody, TableColumn, TableHeader, TableRow, TableCell } from "./dashboard/table-components";
import { SearchIcon, EditIcon, DeleteIcon, ArrowLeftIcon, ArrowRightIcon } from "../assets/dashboard-icon.svg";
import { useState, useEffect, useCallback } from "react";
import ReactDOM from "react-dom";
import DatePicker, { registerLocale } from "react-datepicker";
import sv from "date-fns/locale/sv";
import { fetchCustomers, Customer, CustomerResponse } from "../api/customer";
import { useHolidayRecords } from "../hooks/useHolidayPay";
import { HolidayRecord } from "../api/holidaypayreport";
import { formatAmount, getStatusBadgeClass } from "../helper/common.helper";
import { HolidayPaySidebar } from "./holiday-pay-sidebar.component";
import { HolidayPayoutModal } from "./holiday-pay-payout-modal.component";
import axios from "axios";
import { notify } from "../helper/notify";
import "./holiday-pay-calendar.scss";

/* Register the Swedish locale with react-datepicker once at module
   load — `locale="sv"` then swaps month names, weekday headers
   and screen-reader strings to Swedish. */
registerLocale("sv", sv);

/* Portal the calendar popper to <body> so it escapes the section's
   overflow / transform context and is never clipped. Paired with
   `popperProps.strategy: 'fixed'` for viewport-relative positioning. */
const PopperPortal: React.FC<{ children: React.ReactNode }> = ({ children }) =>
    ReactDOM.createPortal(children, document.body);

/* String <-> Date bridges. handleFromDateSelect / handleToDateSelect
   stay string-only (yyyy-mm-dd ISO) so getHolidayAmount, Redux,
   API filters etc are unchanged. */
const parseIso = (s?: string): Date | null => {
    if (!s) return null;
    const d = new Date(s);
    return isNaN(d.getTime()) ? null : d;
};
const toIso = (d: Date | null): string => {
    if (!d) return "";
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
};

/* Display-only formatter — every rendered date in this section
   shows as yyyy-MM-dd (e.g. 2026-04-15). Kept local instead of
   editing `formatDateForPayout` because that helper is also used
   by other components which still expect the "April 15, 2026"
   long-form output. */
const formatYmd = (value: string | number | Date | null | undefined): string => {
    if (!value) return "";
    const d = value instanceof Date ? value : new Date(value);
    if (isNaN(d.getTime())) return "";
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
};
import { HolidayInvoiceHistoryModal } from "./holiday-pay-invoice-history.component";
import LoadingCard from '../components/SkeletonLoading'
import { PillTabs } from "./dashboard/PillTabs";
import { InvoiceTableSkeleton } from "./dashboard/manage-invoice-skeleton.component";
import { EmptyState } from "./dashboard/EmptyState";

export const HolidayPayReportSection = ({
    className,
}: {
    className?: string
}
) => {
    const { t, i18n } = useTranslation();
    /* Re-evaluates whenever i18next switches language; the picker
       re-renders with Swedish month/weekday labels in sv-se and
       falls back to library default (English) otherwise. */
    const calendarLocale = (i18n.language || "").toLowerCase().startsWith("sv") ? "sv" : undefined;
    const isMobile = window.innerWidth <= 768;
    const [showSidebar, setShowSidebar] = useState<boolean>(true);
    const [fromDate, setFromDate] = useState<string>('');
    const [toDate, setToDate] = useState<string>('');
    const [status, setStatus] = useState<string>('');
    const [showPayoutModal, setShowPayoutModal] = useState<boolean>(false);
    const [payoutLoading, setPayoutLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [appliedFilters, setAppliedFilters] = useState({
        fromDate: '',
        toDate: '',
        status: ''
    });
    const [activeTab, setActiveTab] = useState('transaction-history-label');

    // Shimmer skeleton (~1s) shown each time a sub-tab is opened.
    const [showSkeleton, setShowSkeleton] = useState<boolean>(true);
    useEffect(() => {
        setShowSkeleton(true);
        const timer = setTimeout(() => setShowSkeleton(false), 1000);
        return () => clearTimeout(timer);
    }, [activeTab]);

    const tabs = [
        {
            key: 'transaction-history-label',
            label: t('TRANSACTION_HISTORY'),
            icon: (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
                    <path d="M3 3v5h5" />
                    <path d="M12 7v5l4 2" />
                </svg>
            ),
        },
        {
            key: 'payout-history',
            label: t('PAYOUT_HISTORY_LABEL'),
            icon: (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M12 17V3" />
                    <path d="m6 11 6 6 6-6" />
                    <path d="M19 21H5" />
                </svg>
            ),
        },
    ];

    const [toolTipError, setToolTipError] = useState({
        invalidFromdate: false,
        toDateNotSelected: false,
        fromDateNotSelected: false,
    });
    const {
        calculatedBalance,
        payoutAmount,
        fetchData,
        getHolidayAmount,
        paginatedHolidayRecords,
        totalPages,
        currentPage,
        searchQuery,
        setCurrentPage,
        setSearchQuery,
        goToPreviousPage,
        goToNextPage,
        loading
    } = useHolidayRecords(10);

    useEffect(() => {
        fetchData(currentPage, fromDate, toDate, status);
    }, [fetchData, currentPage, fromDate, toDate, status]);

    // Initial fetch of holiday amount on component mount
    useEffect(() => {
        getHolidayAmount();
    }, [getHolidayAmount]);

    // Debounce the filter changes to avoid too many API calls
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            if (currentPage !== 1) {
                setCurrentPage(1);
            } else {
                fetchData(1, fromDate, toDate, status);
            }
        }, 300);

        return () => clearTimeout(timeoutId);
    }, [fromDate, toDate, status, fetchData]);

    useEffect(() => {
        if (toolTipError.invalidFromdate || toolTipError.toDateNotSelected || toolTipError.fromDateNotSelected) {
            const timer = setTimeout(() => {
                setToolTipError({
                    invalidFromdate: false,
                    toDateNotSelected: false,
                    fromDateNotSelected: false,
                });
            }, 6000);

            return () => clearTimeout(timer);
        }
    }, [toolTipError]);


    const handleFromDateSelect = (date: string) => {
        if (!date) {
            setToolTipError(prev => ({ ...prev, fromDateNotSelected: true }));
            return;
        }

        setFromDate(date);
        setToolTipError({
            invalidFromdate: false,
            toDateNotSelected: false,
            fromDateNotSelected: false,
        });

        // Call getHolidayAmount when both dates are available
        if (date && toDate) {
            getHolidayAmount(date, toDate);
        }
    };

    const handleToDateSelect = (date: string) => {
        if (!date) {
            setToolTipError(prev => ({ ...prev, toDateNotSelected: true }));
            return;
        }

        setToDate(date);
        setToolTipError({
            invalidFromdate: false,
            toDateNotSelected: false,
            fromDateNotSelected: false,
        });

        // Call getHolidayAmount when both dates are available
        if (fromDate && date) {
            getHolidayAmount(fromDate, date);
        }
    };


    const handleStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setStatus(e.target.value);
    };

    const handleClearFilters = () => {
        try {
            setFromDate('');
            setToDate('');
            setStatus('');
            setAppliedFilters({ fromDate: '', toDate: '', status: '' });
            setCurrentPage(1);
            setToolTipError({
                invalidFromdate: false,
                toDateNotSelected: false,
                fromDateNotSelected: false,
            });
        } catch (error) {
            console.error('Error clearing filters:', error);
            notify.error(t('ERROR_CLEARING_FILTERS'));
        }
    };


    const getPageNumbers = () => {
        const pages: (number | string)[] = [];
        const maxPagesToShow = 5;

        if (totalPages <= maxPagesToShow) {
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
        } else {
            pages.push(1);
            if (currentPage > 3) pages.push('...');
            for (
                let i = Math.max(2, currentPage - 1);
                i <= Math.min(totalPages - 1, currentPage + 1);
                i++
            ) {
                if (!pages.includes(i)) pages.push(i);
            }
            if (currentPage < totalPages - 2) pages.push('...');
            pages.push(totalPages);
        }
        return pages;
    };
    return (
        <div className={`holiday-pay-layout ${className || ''}`}>

            <div className="holiday-pay-layout-main">
                <div className="holiday-pay-card-header">
                    <div className="holiday-pay-header-content">
                        <div className="holiday-pay-title-section">
                            <span className="holiday-savings-badge">✨ {t('HOLIDAY_SAVINGS')}</span>
                            <h2 className="holiday-pay-main-title">{t('PAY_OUT_YOUR_OLD_HOLIDAY_PAY')}</h2>
                            <p className="holiday-pay-description">{t('SEE_YOUR_AVAILABLE_HOLIDAY_PAY_AND_REQUEST_A_PAYOUT_ANYTIME')}</p>
                        </div>
                        <button className="pay_out btn btn-primary" onClick={() => setShowPayoutModal(true)}>
                            {t('PAY_OUT')}
                            <span className="pay-out-arrow" aria-hidden="true">→</span>
                        </button>
                    </div>
                </div>
                <div className='px-3 pt-3'>
                    <PillTabs tabs={tabs} activeKey={activeTab} onChange={setActiveTab} />
                </div>
                {showSkeleton && (
                    <div className="invoice-table-container sn-skeleton-host">
                        <InvoiceTableSkeleton columns={activeTab === 'payout-history' ? 9 : 4} filters="full" />
                    </div>
                )}
                {!showSkeleton && activeTab === 'payout-history' && (
                    <div className="invoice-table-container">
                        <div className="invoice-table-header">
                            <h3 className="invoice-table-title">{t('YOUR_HOLIDAY_SAVINGS_PAYOUT')}</h3>
                            <div className="holiday-filter-section">
                                <div className="holiday-filter-inputs">
                                    <div className="holiday-filter-input">
                                        <DatePicker
                                            selected={parseIso(fromDate)}
                                            onChange={(d: Date | null) => handleFromDateSelect(toIso(d))}
                                            dateFormat="yyyy-MM-dd"
                                            placeholderText={t('From')}
                                            className="holiday-filter__input"
                                            wrapperClassName="holiday-filter__input-dp"
                                            popperContainer={PopperPortal}
                                            popperProps={{ strategy: 'fixed' }}
                                            popperClassName="holiday-filter__dp-popper"
                                            calendarClassName="holiday-filter__dp-calendar"
                                            onChangeRaw={(e: React.SyntheticEvent | undefined) => e?.preventDefault()}
                                            showMonthDropdown
                                            showYearDropdown
                                            dropdownMode="scroll"
                                            locale={calendarLocale}
                                            autoComplete="off"
                                            maxDate={new Date()}
                                        />
                                        {toolTipError.fromDateNotSelected && (
                                            <div className="tooltip-error">
                                                {t('PLEASE_SELECT_FROM_DATE')}
                                            </div>
                                        )}
                                        {toolTipError.invalidFromdate && (
                                            <div className="tooltip-error">
                                                {t('FROM_DATE_CANNOT_BE_GREATER_THAN_TO_DATE')}
                                            </div>
                                        )}
                                    </div>
                                    <div className="holiday-filter-input">
                                        <DatePicker
                                            selected={parseIso(toDate)}
                                            onChange={(d: Date | null) => handleToDateSelect(toIso(d))}
                                            dateFormat="yyyy-MM-dd"
                                            placeholderText={t('To')}
                                            className="holiday-filter__input"
                                            wrapperClassName="holiday-filter__input-dp"
                                            popperContainer={PopperPortal}
                                            popperProps={{ strategy: 'fixed' }}
                                            popperClassName="holiday-filter__dp-popper"
                                            calendarClassName="holiday-filter__dp-calendar"
                                            onChangeRaw={(e: React.SyntheticEvent | undefined) => e?.preventDefault()}
                                            showMonthDropdown
                                            showYearDropdown
                                            dropdownMode="scroll"
                                            locale={calendarLocale}
                                            autoComplete="off"
                                            maxDate={new Date()}
                                            minDate={parseIso(fromDate) || undefined}
                                        />
                                        {toolTipError.toDateNotSelected && (
                                            <div className="tooltip-error">
                                                {t('PLEASE_SELECT_TO_DATE')}
                                            </div>
                                        )}
                                    </div>
                                    <div className="holiday-filter-input">
                                        <div className="holiday-filter-input-box">
                                            <select
                                                value={status}
                                                onChange={handleStatusChange}
                                                className="holiday-filter-select"
                                            >
                                                <option value="">{t('Status')}</option>
                                                <option value="1">{t('UNPAID')}</option>
                                                <option value="2">{t('PAID')}</option>
                                            </select>
                                            <div className="holiday-filter-icon">
                                                {/* Chevron down icon */}
                                                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                                                    <path d="M5 8l5 5 5-5" stroke="#000000" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                </svg>
                                            </div>
                                        </div>
                                    </div>
                                    <button className="holiday-filter-clear-btn"
                                        onClick={handleClearFilters}
                                    >
                                        {t('CLEAR')}
                                    </button>
                                </div>
                            </div>
                        </div>
                        <div className="invoice-table-content">
                            {/* Loading State */}

                            {/* Table */}
                            {!loading && !error && paginatedHolidayRecords && paginatedHolidayRecords.length >= 0 && (
                                <div className="holiday-pay-table-scroll-wrapper">
                                    <div className="invoice-table">
                                        {!isMobile && (
                                            <TableHeader>
                                                <TableColumn>ID</TableColumn>
                                                <TableColumn>{t('TOTAL_AMOUNT_REQUESTED')}</TableColumn>
                                                <TableColumn>{t('ADMINISTRATOR_FEE')}</TableColumn>
                                                <TableColumn>{t('AMOUNT')}</TableColumn>
                                                <TableColumn>{t('SOCIAL_FEES')}</TableColumn>
                                                <TableColumn>{t('TAX_AMOUNT')}</TableColumn>
                                                <TableColumn>{t('TO_PAY_AMOUNT')}</TableColumn>
                                                <TableColumn>Status</TableColumn>
                                                <TableColumn>{t('PAYOUT_DATE')}</TableColumn>
                                            </TableHeader>
                                        )}

                                        <TableBody>
                                            {isMobile ? (
                                                // Mobile: Card-based layout
                                                paginatedHolidayRecords?.map((record: HolidayRecord) => (
                                                    <MobileCardWrapper key={record.id || Math.random()}>
                                                        <MobileCardRow label={t('ID') || 'Customer Name'}>
                                                            <span className="invoice-table-detailed__invoice-link">{record.id || 'N/A'}</span>
                                                        </MobileCardRow>
                                                        <MobileCardRow label={t('AMOUNT_REQUESTED')}>
                                                            {formatAmount(record.totalAmountRequested) ?? 'N/A'}
                                                        </MobileCardRow>
                                                        <MobileCardRow label={t('ADMIN_FEE')}>
                                                            {formatAmount(record.administratorFee) ?? 'N/A'}
                                                        </MobileCardRow>
                                                        <MobileCardRow label={t('AMOUNT')}>
                                                            {formatAmount(record.amount) ?? 'N/A'}
                                                        </MobileCardRow>
                                                        <MobileCardRow label={t('SOCIAL_FEES')}>
                                                            {formatAmount(record.socialFee) ?? 'N/A'}
                                                        </MobileCardRow>
                                                        <MobileCardRow label={t('TAX_AMOUNT')}>
                                                            {formatAmount(record.taxAmount) ?? 'N/A'}
                                                        </MobileCardRow>
                                                        <MobileCardRow label={t('TO_PAY_AMOUNT')}>
                                                            {formatAmount(record.amountToPay) ?? 'N/A'}
                                                        </MobileCardRow>
                                                        <MobileCardRow label='Status'>
                                                            <span className={getStatusBadgeClass(record.status)}>
                                                                {record.status === 1 ? t('UNPAID') : record.status === 2 ? t('PAID') : 'N/A'}
                                                            </span>
                                                        </MobileCardRow>
                                                        <MobileCardRow label={t('PAYOUT_DATE')}>
                                                            {record?.status === 2 && record?.paidDate
                                                                ? formatYmd(record.paidDate)
                                                                : 'N/A'}
                                                        </MobileCardRow>
                                                    </MobileCardWrapper>
                                                ))
                                            ) : (
                                                paginatedHolidayRecords.map((record: HolidayRecord) => (
                                                    <TableRow key={record.id || Math.random()}>
                                                        <TableCell>
                                                            <span className="invoice-table-detailed__invoice-link">{record.id || 'N/A'}</span>
                                                        </TableCell>
                                                        <TableCell>{formatAmount(record.totalAmountRequested) ?? 'N/A'}</TableCell>
                                                        <TableCell>{formatAmount(record.administratorFee) ?? 'N/A'}</TableCell>
                                                        <TableCell>{formatAmount(record.amount) ?? 'N/A'}</TableCell>
                                                        <TableCell>{formatAmount(record.socialFee) ?? 'N/A'}</TableCell>
                                                        <TableCell>{formatAmount(record.taxAmount) ?? 'N/A'}</TableCell>
                                                        <TableCell><span className="sn-amount">{formatAmount(record.amountToPay) ?? 'N/A'}</span></TableCell>
                                                        <TableCell>
                                                            <span className={getStatusBadgeClass(record.status)}>
                                                                {record.status === 1 ? t('UNPAID') : record.status === 2 ? t('PAID') : 'N/A'}
                                                            </span>
                                                        </TableCell>
                                                        <TableCell>
                                                            {record?.status === 2 && record?.paidDate
                                                                ? formatYmd(record.paidDate)
                                                                : 'N/A'}
                                                        </TableCell>
                                                    </TableRow>
                                                ))
                                            )}
                                        </TableBody>
                                    </div>
                                </div>
                            )}

                            {/* Empty state — full width, outside the display:table */}
                            {!loading && !error && paginatedHolidayRecords.length === 0 && (
                                <EmptyState
                                    title={t('NO_HISTORY_RECORDS_FOUND')}
                                    subtitle={t('NO_DATA_SUBTITLE')}
                                />
                            )}

                            {/* Pagination */}
                            {totalPages > 1 && paginatedHolidayRecords && paginatedHolidayRecords.length > 0 && (
                                <div className="invoice-table-pagination">
                                    <div className="pagination-controls">
                                        <button
                                            className="pagination-button pagination-button--prev"
                                            onClick={goToPreviousPage}
                                            disabled={currentPage === 1}
                                        >
                                            <ArrowLeftIcon />
                                            <span>{t('PREV_STEP') || 'Prev'}</span>
                                        </button>

                                        {getPageNumbers().map((page, index) => {
                                            if (page === '...') {
                                                return (
                                                    <button
                                                        key={`ellipsis-${index}`}
                                                        className="pagination-button pagination-button--number"
                                                        disabled
                                                    >
                                                        ...
                                                    </button>
                                                );
                                            }
                                            return (
                                                <button
                                                    key={page}
                                                    className={`pagination-button pagination-button--number${page === currentPage ? ' pagination-button--active' : ''
                                                        }`}
                                                    onClick={() => setCurrentPage(page as number)}
                                                >
                                                    {page}
                                                </button>
                                            );
                                        })}

                                        <button
                                            className="pagination-button pagination-button--next"
                                            onClick={goToNextPage}
                                            disabled={currentPage === totalPages}
                                        >
                                            <span>{t('NEXT_STEP') || 'Next'}</span>
                                            <ArrowRightIcon />
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}
                {!showSkeleton && activeTab === 'transaction-history-label' && (
                    <HolidayInvoiceHistoryModal />
                )}

            </div>
            <HolidayPayoutModal
                isOpen={showPayoutModal}
                onClose={() => setShowPayoutModal(false)}
                onConfirm={payoutAmount}
                maxAmount={calculatedBalance?.response?.holidaySavingsAmount || 0}
                currency="SEK"
                loading={payoutLoading}
            />
        </div>
    );
}